import { useEffect, useState } from 'react';
import { assistantRuleSave, type ApprovalItem, type ApprovalReversibility } from '@aithora/shared';
import { useSession } from '../session/SessionContext';
import { useReadOnlyReason } from '../ui/ReadOnly';
import { sx } from '../ui/designStyles';
import { hm, useAiControls } from './aiControls';

/**
 * "Needs your OK" (Console V4, Spec V4 section 16 B; design T19051-19077, values
 * L35831-35956): the one approval queue, at the top of the Atlas panel and on Guardian's
 * Approvals. The same state everywhere, so a decision clears it everywhere.
 *
 *   LOW    two or more collapse into one checklist, all ticked, one "Approve N"
 *   CHECK  a single card (Aithora-side items; only Super Admin and Operator see them)
 *   CARE   stands alone, never batched: Approve, then confirm with Face ID or a passkey
 *
 * The person who proposed a CHECK or CARE item sees "Needs someone else" (the server
 * refuses it too). Edit changes the action itself (its editable fields, from the server):
 * what is saved is what runs, re-checked at the governance gate, and the card says who
 * edited it and what Atlas first proposed;
 * Decline takes an optional reason that Atlas keeps. While the AI is paused an approval
 * records and runs when the AI is back on. Every decision is sealed to the Truth Ledger.
 *
 * Console V5 (design T6286-6406, apq values V5 L42330-42480): each card adds what the server
 * sends of Because, how hard it is to undo, FROM AN EMAIL when a detail came from outside,
 * Expires and the owner's OK for spend over the limit; a row is drawn only when the server
 * sends it. "Always allow this" saves a rule (POST /assistant/rules) when the server offers
 * one. An approved email waits 60 seconds in a SENDING strip with Undo. A text's tap link
 * (/#approve=<id>) puts that request first, outlined, under "Opened from your text".
 */

const TIER: Record<ApprovalItem['tier'], [string, string, string]> = {
  low: ['LOW RISK', '#12503C', 'rgba(31,107,82,.14)'],
  check: ['CHECK', '#2F5C97', 'rgba(47,92,151,.12)'],
  care: ['CARE · STANDS ALONE', '#8A1C16', 'rgba(179,38,30,.11)'],
};
const D_TONE: Record<'APPROVED' | 'DECLINED', [string, string]> = { APPROVED: ['#12503C', 'rgba(31,107,82,.12)'], DECLINED: ['#8A1C16', 'rgba(179,38,30,.1)'] };
const REV: Record<ApprovalReversibility, [string, string, string]> = { undo: ['UNDOABLE', '#12503C', 'rgba(31,107,82,.13)'], hard: ['HARD TO UNDO', '#7A4A0E', 'rgba(194,112,47,.16)'], perm: ['PERMANENT', '#8A1C16', 'rgba(179,38,30,.11)'] };
const CHIP = 'font-size:14px;font-weight:800;letter-spacing:.06em;border-radius:5px;padding:2px 7px;white-space:nowrap;';
const SPEND_TAG: [string, string, string] = ['SPEND · NEEDS THE OWNER', '#8A1C16', 'rgba(179,38,30,.11)'];
const BTN_EDIT = 'display:inline-flex;align-items:center;height:32px;padding:0 12px;border-radius:8px;border:1px solid rgba(47,92,151,.4);color:#2F5C97;font-size:16px;font-weight:700;text-decoration:none;';
const BTN_DECL = 'display:inline-flex;align-items:center;height:32px;padding:0 12px;border-radius:8px;border:1px solid rgba(179,38,30,.35);color:#B3261E;font-size:16px;font-weight:700;text-decoration:none;';
const BTN_CANCEL = 'display:inline-flex;align-items:center;height:32px;padding:0 10px;color:#54606C;font-size:16px;font-weight:600;text-decoration:none;';
const INPUT = 'flex:1 1 200px;min-width:0;height:32px;padding:0 10px;border-radius:8px;background:#fff;font:inherit;font-size:16px;color:#22272E;box-sizing:border-box;';

/** The queue block. `emptyHidden` draws nothing at all while nothing waits (Guardian). */
export function ApprovalQueue({ emptyHidden = false }: { emptyHidden?: boolean }) {
  const ai = useAiControls();
  const { client } = useSession();
  const roReason = useReadOnlyReason();
  const [allowing, setAllowing] = useState<string | null>(null);
  const [, tick] = useState(0);
  const sendingN = ai?.approvals.data?.sending?.length ?? 0;
  useEffect(() => {
    if (!sendingN) return;
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [sendingN]);
  const [editing, setEditing] = useState<string | null>(null);
  const [editVals, setEditVals] = useState<Record<string, string>>({});
  const [editWhy, setEditWhy] = useState('');
  const [declining, setDeclining] = useState<string | null>(null);
  const [why, setWhy] = useState('');
  const [stepping, setStepping] = useState<string | null>(null);
  const [unpick, setUnpick] = useState<Record<string, boolean>>({});
  const q = ai?.approvals.data;
  if (!ai || !ai.queueRole || !q || !q.can) return null;
  const pend = q.items;
  if (emptyHidden && pend.length === 0) return null;
  const paused = q.paused;
  const me = ai.status.data?.viewer.userId ?? null;
  const lows = pend.filter((x) => x.tier === 'low');
  const picked = lows.filter((x) => !unpick[x.id]);
  const careN = pend.filter((x) => x.tier === 'care').length;
  const streak = q.habits.streak;
  const write = (fn: () => void) => (e: { preventDefault: () => void }) => {
    e.preventDefault();
    if (roReason) return;
    fn();
  };
  const cancel = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setDeclining(null);
    setEditing(null);
    setStepping(null);
    setAllowing(null);
  };
  const owner = !!ai.status.data?.viewer.owner;
  const focus = ai.focusId;
  const focused = focus ? pend.find((x) => x.id === focus) ?? null : null;
  const cards = pend
    .filter((x) => x.tier !== 'low' || lows.length < 2 || x.id === focus)
    .sort((a, b) => Number(b.id === focus) - Number(a.id === focus));
  const sending = (q.sending ?? []).map((x) => ({ ...x, left: Math.max(0, Math.ceil((Date.parse(x.sendsAt) - Date.now()) / 1000)) })).filter((x) => x.left > 0);
  const batchShow = lows.length >= 2 && !(focused && focused.tier !== 'low');
  return (
    <div data-screen-label="Needs your OK" style={sx('display:flex;flex-direction:column;gap:8px;min-width:0;')}>
      <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;')}>
        <span style={sx('font-size:13.5px;font-weight:800;color:#22272E;')}>Needs your OK</span>
        <span style={sx('font-size:14px;color:#54606C;')}>{pend.length ? pend.length + ' waiting' + (careN ? ' · ' + careN + ' care item' + (careN === 1 ? '' : 's') : '') : 'all clear'}</span>
        <span style={sx('flex:1 1 20px;')}></span>
        <span style={sx('font-size:14px;color:#54606C;')}>one queue · clears everywhere</span>
      </div>
      {paused && pend.length > 0 && (
        <div style={sx('font-size:14px;color:#8A1C16;background:#F6E3E1;border-radius:8px;padding:7px 10px;line-height:1.45;')}>AI is paused. Anything you approve waits and runs when the AI is back on.</div>
      )}
      {streak >= 4 && (
        <div style={sx('font-size:14px;color:#3C454F;background:rgba(47,92,151,.08);border-radius:8px;padding:7px 10px;line-height:1.45;')}>
          {'You have approved the last ' + streak + ' without a change. Take a second look at what each one changes and who it tells.'}
        </div>
      )}
      {ai.error && <div role="alert" style={sx('font-size:14px;color:#B3261E;line-height:1.45;')}>{ai.error}</div>}
      {sending.map((d) => (
        <div key={d.proposalId} style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:4px;border:1px solid rgba(194,112,47,.4);border-radius:10px;padding:9px 12px;background:rgba(194,112,47,.08);')}>
          <span style={sx('font-size:14px;font-weight:800;letter-spacing:.06em;border-radius:5px;padding:1px 6px;white-space:nowrap;color:#7A4A0E;background:rgba(194,112,47,.16);')}>SENDING</span>
          <span style={sx('flex:1 1 200px;min-width:0;font-size:13px;font-weight:600;color:#22272E;')}>{'Sending in 0:' + String(Math.min(59, d.left)).padStart(2, '0') + ' · ' + d.label}</span>
          <a href="#" aria-disabled={!!roReason || undefined} title={roReason ?? undefined} onClick={write(() => void ai.undo(d.proposalId))} style={sx('display:inline-flex;align-items:center;height:30px;padding:0 14px;border-radius:8px;font-size:13px;font-weight:800;text-decoration:none;background:#22272E;color:#fff;')}>Undo</a>
        </div>
      ))}
      {focused && (
        <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;font-size:16px;color:#22272E;background:rgba(47,92,151,.09);border-radius:8px;padding:8px 10px;line-height:1.45;')}>
          <span style={sx('font-weight:700;')}>Opened from your text.</span>
          <span style={sx('flex:1 1 160px;min-width:0;color:#3C454F;')}>{'It’s at the top: ' + focused.label + '.'}</span>
          <a href="#" onClick={(e) => { e.preventDefault(); ai.clearFocus(); }} style={sx('font-size:14px;font-weight:700;color:#2F5C97;text-decoration:none;')}>Done</a>
        </div>
      )}
      {batchShow && (
        <div style={sx('border:1px solid rgba(31,107,82,.28);border-radius:10px;padding:10px 12px;display:flex;flex-direction:column;gap:6px;background:rgba(31,107,82,.05);')}>
          <div style={sx('display:flex;align-items:center;gap:8px;')}>
            <span style={sx('font-size:14px;font-weight:800;letter-spacing:.07em;color:#12503C;background:rgba(31,107,82,.14);border-radius:5px;padding:2px 7px;white-space:nowrap;flex:0 0 auto;')}>LOW RISK</span>
            <span style={sx('font-size:16px;font-weight:700;color:#22272E;flex:1 1 auto;min-width:0;')}>{lows.length + ' small items, same kind of change'}</span>
          </div>
          {lows.map((b) => (
            <a
              key={b.id}
              href="#"
              role="checkbox"
              aria-checked={!unpick[b.id]}
              onClick={(e) => { e.preventDefault(); setUnpick({ ...unpick, [b.id]: !unpick[b.id] }); }}
              style={sx('display:flex;align-items:flex-start;gap:8px;text-decoration:none;color:#22272E;font-size:16px;line-height:1.4;padding:2px 0;')}
            >
              <span style={sx('display:inline-flex;align-items:center;justify-content:center;width:16px;height:16px;flex:0 0 16px;margin-top:1px;border-radius:4px;font-size:14px;font-weight:800;' + (unpick[b.id] ? 'border:1.5px solid rgba(34,39,46,.3);' : 'background:#1F6B52;color:#fff;'))}>{unpick[b.id] ? '' : '✓'}</span>
              <span style={sx('flex:1 1 auto;min-width:0;')}>
                {b.label}
                <span style={sx('display:block;font-size:14px;color:#54606C;')}>{b.changes + ' Undo: ' + b.undo}</span>
              </span>
            </a>
          ))}
          <a
            href="#"
            aria-disabled={!picked.length || !!roReason || undefined}
            title={roReason ?? undefined}
            onClick={write(() => { if (picked.length) void ai.approveBatch(picked.map((x) => x.id)); })}
            style={sx('align-self:flex-start;display:inline-flex;align-items:center;height:32px;padding:0 14px;border-radius:8px;background:#1F6B52;color:#fff;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;')}
          >
            {picked.length ? 'Approve ' + picked.length + (paused ? ' · runs when AI is back on' : '') : 'Pick at least one'}
          </a>
        </div>
      )}
      {cards.map((c) => {
        const spend = !!c.ownerOk;
        const t = spend ? SPEND_TAG : TIER[c.tier];
        const isEditing = editing === c.id;
        const isDeclining = declining === c.id;
        const isStepping = stepping === c.id;
        const isAllowing = allowing === c.id;
        // Spend over the owner's limit waits for the owner's OK; everyone else sees it wait.
        const ownerWait = spend && !owner;
        const blocked = c.selfBlocked || ownerWait;
        const approveLabel = ownerWait ? 'Waiting for Andrew’s OK' : spend ? 'OK the spend' : blocked ? 'Needs someone else' : paused ? 'Approve · runs when AI is back on' : 'Approve';
        const needsStep = c.needsPasskey || spend;
        const canAllow = !!c.rule && !c.untrusted && c.tier !== 'care' && !spend && !!ai.status.data?.viewer.team;
        const chips: [string, string, string][] = [];
        if (c.reversibility && REV[c.reversibility]) chips.push(REV[c.reversibility]);
        if (c.untrusted) chips.push(['FROM AN EMAIL · CHECK THE DETAILS', '#5B3A8C', 'rgba(122,106,174,.16)']);
        const rows: [string, string][] = [];
        if (c.why) rows.push(['Because', c.why]);
        rows.push(['Changes', c.changes], ['Tells', c.tells], ['Undo', c.undo]);
        if (c.untrusted) rows.push(['Check', 'These details came from an email. An email never decides who, how much or where without your fresh OK, and a rule can’t cover it.']);
        if (c.expiresNote) rows.push(['Expires', c.expiresNote.replace(/^Expires /, '').replace(/^in /, 'In ').replace(/^tonight/, 'Tonight')]);
        if (spend) rows.push(['Owner OK', owner ? 'Over the limit, so it needs your OK.' : 'Over the limit, so it waits for Andrew’s OK. Nothing is paid until he OKs it.']);
        return (
          <div key={c.id} style={sx((c.id === focus ? 'box-shadow:0 0 0 2px rgba(47,92,151,.45);' : '') + 'border:1px solid ' + (c.tier === 'care' ? 'rgba(179,38,30,.3)' : 'rgba(34,39,46,.14)') + ';border-radius:10px;padding:10px 12px;display:flex;flex-direction:column;gap:7px;background:#FCFAF4;')}>
            <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;')}>
              <span style={sx('font-size:14px;font-weight:800;letter-spacing:.07em;border-radius:5px;padding:2px 7px;white-space:nowrap;color:' + t[1] + ';background:' + t[2] + ';')}>{t[0]}</span>
              <span style={sx('font-size:13px;font-weight:700;color:#22272E;flex:1 1 160px;min-width:0;text-wrap:pretty;')}>{c.label}</span>
              <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>{(c.mine ? 'you' : c.proposer.name) + ' · ' + c.building.name}</span>
            </div>
            {chips.length > 0 && (
              <div style={sx('display:flex;gap:6px;flex-wrap:wrap;')}>
                {chips.map((ch) => <span key={ch[0]} style={sx(CHIP + 'color:' + ch[1] + ';background:' + ch[2] + ';')}>{ch[0]}</span>)}
              </div>
            )}
            <div style={sx('display:grid;grid-template-columns:76px minmax(0,1fr);gap:3px 8px;font-size:14px;line-height:1.45;')}>
              {rows.map(([k, v]) => (
                <span key={k} style={{ display: 'contents' }}>
                  <span style={sx('color:#54606C;font-weight:600;')}>{k}</span>
                  <span style={sx('color:#3C454F;')}>{v}</span>
                </span>
              ))}
            </div>
            {c.edited && (
              <div style={sx('font-size:14px;color:#54606C;line-height:1.45;')}>
                {'Edited by ' + (c.editedBy ? (c.editedBy.id && c.editedBy.id === me ? 'you' : c.editedBy.name) : 'a team member') + (c.editedAt ? ' ' + hm(c.editedAt) : '') + '. This runs as edited.'}
                {c.originalLabel && <span style={sx('display:block;')}>{'Atlas proposed: ' + c.originalLabel}</span>}
              </div>
            )}
            {isEditing && (
              <div style={sx('display:flex;flex-direction:column;gap:6px;')}>
                {c.editable.map((f) => {
                  const v = editVals[f.key] ?? f.value;
                  const set = (val: string) => setEditVals({ ...editVals, [f.key]: val });
                  const border = 'border:1px solid rgba(47,92,151,.4);';
                  return (
                    <label key={f.key} style={sx('display:flex;flex-direction:column;gap:3px;font-size:14px;font-weight:600;color:#54606C;')}>
                      {f.label}
                      {f.kind === 'longtext' ? (
                        <textarea aria-label={f.label} value={v} maxLength={f.max} rows={3} onChange={(e) => set(e.target.value)} style={sx('min-width:0;padding:6px 10px;border-radius:8px;background:#fff;font:inherit;font-size:16px;font-weight:400;color:#22272E;box-sizing:border-box;resize:vertical;' + border)} />
                      ) : f.kind === 'choice' ? (
                        <select aria-label={f.label} value={v} onChange={(e) => set(e.target.value)} style={sx(INPUT + 'flex:0 0 auto;font-weight:400;' + border)}>
                          {(f.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : (
                        <input type="text" aria-label={f.label} value={v} maxLength={f.max} inputMode={f.kind === 'integer' ? 'numeric' : undefined} onChange={(e) => set(e.target.value)} style={sx(INPUT + 'flex:0 0 auto;font-weight:400;' + border)} />
                      )}
                    </label>
                  );
                })}
                <input type="text" aria-label="Why the change?" value={editWhy} onChange={(e) => setEditWhy(e.target.value)} placeholder="Why the change? (kept on the Truth Ledger)" style={sx(INPUT + 'flex:0 0 auto;border:1px solid rgba(34,39,46,.18);')} />
                <div style={sx('display:flex;gap:6px;flex-wrap:wrap;align-items:center;')}>
                  <a
                    href="#"
                    onClick={write(() => {
                      const changed: Record<string, string> = {};
                      for (const f of c.editable) if (editVals[f.key] !== undefined && editVals[f.key] !== f.value) changed[f.key] = editVals[f.key];
                      const r = editWhy.trim();
                      setEditing(null);
                      setEditVals({});
                      setEditWhy('');
                      if (Object.keys(changed).length) void ai.decide(c.id, 'edit', { fields: changed, ...(r ? { reason: r } : {}) });
                    })}
                    style={sx('display:inline-flex;align-items:center;height:32px;padding:0 12px;border-radius:8px;background:#2F5C97;color:#fff;font-size:16px;font-weight:700;text-decoration:none;')}
                  >
                    Save change
                  </a>
                  <a href="#" onClick={cancel} style={sx(BTN_CANCEL)}>Cancel</a>
                  <span style={sx('font-size:14px;color:#54606C;')}>What you save is what runs. It is checked again before anyone can approve it.</span>
                </div>
              </div>
            )}
            {isDeclining && (
              <div style={sx('display:flex;gap:6px;flex-wrap:wrap;')}>
                <input type="text" aria-label="Why not?" value={why} onChange={(e) => setWhy(e.target.value)} placeholder="Why not? Atlas learns from this" style={sx(INPUT + 'border:1px solid rgba(179,38,30,.35);')} />
                <a
                  href="#"
                  onClick={write(() => {
                    const r = why.trim();
                    setDeclining(null);
                    setWhy('');
                    void ai.decide(c.id, 'decline', r ? { reason: r } : {});
                  })}
                  style={sx('display:inline-flex;align-items:center;height:32px;padding:0 12px;border-radius:8px;background:#B3261E;color:#fff;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;')}
                >
                  Confirm decline
                </a>
                <a href="#" onClick={cancel} style={sx(BTN_CANCEL)}>Cancel</a>
              </div>
            )}
            {isStepping && (
              <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;background:rgba(47,92,151,.08);border-radius:8px;padding:8px 10px;')}>
                <span style={sx('font-size:14px;color:#22272E;flex:1 1 160px;min-width:0;line-height:1.4;')}>{spend ? 'Spend over the limit. Confirm it’s you; the live price is checked again before paying.' : 'Care action. Confirm it’s you before it runs.'}</span>
                <a
                  href="#"
                  onClick={write(() => {
                    setStepping(null);
                    void ai.decide(c.id, 'approve', { passkey: true });
                  })}
                  style={sx('display:inline-flex;align-items:center;height:32px;padding:0 12px;border-radius:8px;background:#2F5C97;color:#fff;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;')}
                >
                  Confirm with Face ID or passkey
                </a>
                <a href="#" onClick={cancel} style={sx(BTN_CANCEL)}>Cancel</a>
              </div>
            )}
            {isAllowing && c.rule && (
              <div style={sx('display:flex;flex-direction:column;gap:6px;background:rgba(31,107,82,.07);border-radius:8px;padding:9px 11px;')}>
                <span style={sx('font-size:14px;font-weight:800;letter-spacing:.07em;color:#12503C;')}>NEW RULE · WHAT IT COVERS</span>
                <span style={sx('font-size:16px;color:#22272E;line-height:1.45;text-wrap:pretty;')}>{'Atlas may ' + c.rule.sentence.replace(/^Always allow /i, '') + ', for you, for 30 days. It still asks if a recipient is new or it runs 10 times in an hour.'}</span>
                {c.rule.track && <span style={sx('font-size:14px;color:#3C454F;')}>{c.rule.track}</span>}
                <span style={sx('font-size:14px;color:#54606C;')}>Money, care, paging and the kill switch can never be allowed. Revoke any time in Settings, Atlas Assistant.</span>
                <div style={sx('display:flex;gap:6px;flex-wrap:wrap;')}>
                  <a
                    href="#"
                    onClick={write(() => {
                      const rule = c.rule!;
                      setAllowing(null);
                      void ai.decide(c.id, 'approve', { passkey: true }).then(() =>
                        assistantRuleSave(client, { body: { tool: rule.tool, mode: 'allow', ...(rule.conditions ? { conditions: rule.conditions } : {}), hours: 720 } }).catch(() => undefined));
                    })}
                    style={sx('display:inline-flex;align-items:center;height:32px;padding:0 12px;border-radius:8px;background:#1F6B52;color:#fff;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;')}
                  >
                    Approve and save rule · passkey
                  </a>
                  <a href="#" onClick={cancel} style={sx(BTN_CANCEL)}>Cancel</a>
                </div>
              </div>
            )}
            {!isStepping && !isDeclining && !isEditing && !isAllowing && (
              <div style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;')}>
                <a
                  href="#"
                  aria-disabled={blocked || !!roReason || undefined}
                  title={roReason ?? undefined}
                  onClick={write(() => {
                    if (blocked) return;
                    if (needsStep) setStepping(c.id);
                    else void ai.decide(c.id, 'approve');
                  })}
                  style={sx('display:inline-flex;align-items:center;height:32px;padding:0 14px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' + (blocked ? 'background:rgba(34,39,46,.07);color:#54606C;cursor:default;' : 'background:#1F6B52;color:#fff;'))}
                >
                  {approveLabel}
                </a>
                {c.editable.length > 0 && !blocked && (
                  <a href="#" onClick={(e) => { e.preventDefault(); setEditing(isEditing ? null : c.id); setEditVals({}); setEditWhy(''); setDeclining(null); }} style={sx(BTN_EDIT)}>Edit</a>
                )}
                <a href="#" onClick={(e) => { e.preventDefault(); setDeclining(isDeclining ? null : c.id); setEditing(null); setAllowing(null); }} style={sx(BTN_DECL)}>Decline</a>
                {canAllow && (
                  <a href="#" onClick={(e) => { e.preventDefault(); setAllowing(c.id); setEditing(null); setDeclining(null); }} style={sx('display:inline-flex;align-items:center;height:32px;padding:0 10px;color:#1F6B52;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;')}>Always allow this…</a>
                )}
                {ownerWait && <span style={sx('font-size:14px;color:#54606C;')}>Andrew gets a text and a push. He OKs it by YES with its code or here.</span>}
                {blocked && !ownerWait && <span style={sx('font-size:14px;color:#54606C;')}>{(c.mine ? 'You proposed this.' : 'You edited this.') + ' A different person approves care and higher-risk items.'}</span>}
              </div>
            )}
          </div>
        );
      })}
      {pend.length === 0 && <div style={sx('font-size:16px;color:#54606C;padding:4px 0;')}>Nothing waiting for you. Atlas will ask here before it acts.</div>}
      {q.recent.length > 0 && (
        <div style={sx('display:flex;flex-direction:column;gap:3px;padding-top:6px;border-top:1px solid rgba(34,39,46,.08);')}>
          {q.recent.map((d) => (
            <div key={d.proposalId + d.at} style={sx('display:flex;align-items:baseline;gap:8px;font-size:14px;')}>
              <span style={sx('font-size:14px;font-weight:800;letter-spacing:.06em;border-radius:5px;padding:1px 6px;white-space:nowrap;color:' + D_TONE[d.state][0] + ';background:' + D_TONE[d.state][1] + ';')}>{d.state}</span>
              <span style={sx('flex:1 1 auto;min-width:0;color:#3C454F;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;')}>{d.label + (d.reason ? ' · ' + d.reason : '')}</span>
              <span style={sx('color:#54606C;white-space:nowrap;')}>{(d.by.id && d.by.id === me ? 'you' : d.by.name) + ' ' + hm(d.at) + (d.passkey ? ' · passkey' : '') + ' · ledger'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
